<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import { apiClient } from '@/utils/api';
  import PublicProfileSubpageShell from '@/components/profile/PublicProfileSubpageShell.svelte';
  import type { ThemeConfig, ProfileThemeName } from '@/lib/profile-themes';

  interface FollowProfile {
    display_name: string;
    username: string;
    avatar_initial: string;
    avatar_url?: string;
  }

  interface FollowUser {
    id: string;
    name: string;
    nickname?: string;
    avatar_url?: string;
    username?: string;
    /** 서버 합성 문자열(소속 / 부서 / 팀) — `follow_user_json`. 개별 필드가 아니다. */
    department?: string;
  }

  interface Pagination {
    current_page: number;
    total_pages: number;
    total_count: number;
  }

  interface Theme extends ThemeConfig {
    template?: ProfileThemeName;
  }

  let {
    profile, users = [], pagination, tab, counts,
    theme = { template: 'clean' },
    my_following_ids = [], is_authenticated = false, current_user_id = null,
  }: {
    profile: FollowProfile; users: FollowUser[]; pagination: Pagination;
    tab: 'followers' | 'following'; counts: { followers: number; following: number };
    theme?: Theme;
    my_following_ids?: string[]; is_authenticated?: boolean; current_user_id?: string | null;
  } = $props();

  // svelte-ignore state_referenced_locally
  const initialFollowingIds = my_following_ids;
  let followingSet = $state(new Set(initialFollowingIds));
  let loadingIds = $state(new Set<string>());

  function isFollowing(userId: string) { return followingSet.has(userId); }

  // 🔴 팔로우 API 계약 — 생성·삭제 모두 **팔로우 대상 사용자 id** 를 쓴다.
  //    POST   /api/v1/follows        body { userId }  → 서버 params[:user_id]
  //    DELETE /api/v1/follows/:id    :id = 대상 user id (follow.id 아님)
  //    근거: app/controllers/api/v1/follows_controller.rb 의 create/destroy.
  //    follow.id 를 넘기면 User.find 가 못 찾아 404 가 된다. FollowButton.svelte 와 동일 계약.
  async function toggleFollow(userId: string) {
    if (!is_authenticated || loadingIds.has(userId)) return;
    loadingIds.add(userId); loadingIds = new Set(loadingIds);
    try {
      // apiClient 는 4xx/5xx 에 throw 하지 않고 { success: false } 를 반환한다
      // (utils/api.ts). 성공을 확인하지 않으면 실패한 요청에도 UI 가 바뀐다.
      if (isFollowing(userId)) {
        const r = await apiClient.delete(`/api/v1/follows/${userId}`);
        if (r.success) { followingSet.delete(userId); followingSet = new Set(followingSet); }
      } else {
        const r = await apiClient.post('/api/v1/follows', { userId });
        if (r.success) { followingSet.add(userId); followingSet = new Set(followingSet); }
      }
    } finally { loadingIds.delete(userId); loadingIds = new Set(loadingIds); }
  }

  function navigatePage(p: number) { router.visit(`/@${profile.username}/${tab}?page=${p}`); }
  function getUserInitial(u: FollowUser) { return u.nickname?.[0] || u.name?.[0] || '?'; }
  function getDisplayName(u: FollowUser) { return u.nickname || u.name; }
  function hasSecondaryName(u: FollowUser) { return !!u.nickname && u.nickname !== u.name; }
</script>

<svelte:head>
  <title>{profile.display_name}의 {tab === 'followers' ? '팔로워' : '팔로잉'} | AX Hub</title>
</svelte:head>

<PublicProfileSubpageShell
  {profile}
  {theme}
  backHref={`/@${profile.username}`}
  title={`${profile.display_name}님의 관계`}
  subtitle={tab === 'followers' ? `팔로워 ${counts.followers}명` : `팔로잉 ${counts.following}명`}
  activeTab={tab}
  width="5xl"
  tabs={[
    { key: 'followers', label: '팔로워', href: `/@${profile.username}/followers`, count: counts.followers },
    { key: 'following', label: '팔로잉', href: `/@${profile.username}/following`, count: counts.following },
  ]}
>
    {#if users.length === 0}
      <div class="text-center py-16">
        <p class="public-subpage-faint">{tab === 'followers' ? '아직 팔로워가 없습니다' : '아직 팔로잉하는 사람이 없습니다'}</p>
      </div>
    {:else}
      <div class="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {#each users as user (user.id)}
          <a href={user.username ? `/@${user.username}` : '#'}
            class="public-subpage-card flex items-center gap-4 p-4 transition-all">
            {#if user.avatar_url}
              <img src={user.avatar_url} alt={getDisplayName(user)}
                class="w-10 h-10 shrink-0 rounded-full object-cover" />
            {:else}
              <div class="public-follows__fallback-avatar w-10 h-10 shrink-0 text-sm font-medium">
                {getUserInitial(user)}
              </div>
            {/if}
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-1.5">
                <span class="truncate text-sm font-semibold">{getDisplayName(user)}</span>
                {#if hasSecondaryName(user)}<span class="public-subpage-faint truncate text-xs">{user.name}</span>{/if}
              </div>
              {#if user.department}<p class="public-subpage-muted mt-0.5 truncate text-xs">{user.department}</p>{/if}
            </div>
            {#if is_authenticated && user.id !== current_user_id}
              <button
                onclick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFollow(user.id); }}
                disabled={loadingIds.has(user.id)}
                class:public-follows__button--following={isFollowing(user.id)}
                class:public-follows__button--default={!isFollowing(user.id)}
                class="public-follows__button shrink-0 px-4 py-1.5 text-xs font-medium disabled:opacity-50">
                {loadingIds.has(user.id) ? '...' : isFollowing(user.id) ? '팔로잉' : '팔로우'}
              </button>
            {/if}
          </a>
        {/each}
      </div>
      {#if pagination.total_pages > 1}
        <div class="mt-8 flex items-center justify-center gap-3">
          <button class="public-pagination-button px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-40"
            disabled={pagination.current_page <= 1} onclick={() => navigatePage(pagination.current_page - 1)}>이전</button>
          <span class="public-subpage-muted text-sm">{pagination.current_page} / {pagination.total_pages}</span>
          <button class="public-pagination-button px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-40"
            disabled={pagination.current_page >= pagination.total_pages} onclick={() => navigatePage(pagination.current_page + 1)}>다음</button>
        </div>
      {/if}
    {/if}
</PublicProfileSubpageShell>

<style>
  .public-follows__fallback-avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    background: color-mix(in srgb, var(--accent, #111827) 12%, transparent);
    color: var(--accent, #111827);
  }

  .public-follows__button {
    border-radius: 9999px;
    border: 1px solid var(--button-border, var(--accent, #111827));
    transition: transform 150ms ease, background 150ms ease, color 150ms ease, border-color 150ms ease;
  }

  .public-follows__button:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  .public-follows__button--default {
    background: var(--button-bg, var(--accent, #111827));
    color: var(--button-text, #ffffff);
  }

  .public-follows__button--following {
    background: transparent;
    color: var(--text-secondary, #6b7280);
    border-color: var(--card-border, rgba(15, 23, 42, 0.14));
  }

  .public-follows__button--following:hover:not(:disabled) {
    color: #ef4444;
    border-color: color-mix(in srgb, #ef4444 32%, transparent);
  }

  .public-pagination-button {
    color: var(--text-primary, #111827);
    background: var(--card-bg, rgba(255, 255, 255, 0.92));
    border: 1px solid var(--card-border, rgba(15, 23, 42, 0.1));
    border-radius: 9999px;
    transition: background 150ms ease, border-color 150ms ease, transform 150ms ease;
  }

  .public-pagination-button:hover:not(:disabled) {
    transform: translateY(-1px);
    background: var(--card-hover, rgba(255, 255, 255, 0.96));
    border-color: color-mix(in srgb, var(--accent, #111827) 24%, transparent);
  }
</style>
